"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"

export type BrandDirectoryEntry = {
  name: string
  slug: string
  verifiedVendor?: boolean
  reviewSummary?: {
    totalReviews?: number | null
    averageOverall?: number | null
    vendorReputationScore?: number | null
    recommendationRate?: number | null
  } | null
}

const LETTERS = Array.from({ length: 26 }, (_, index) =>
  String.fromCharCode(65 + index)
)
const OTHER_KEY = "#"

const getLetterKey = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return OTHER_KEY
  const firstChar = trimmed.charAt(0).toUpperCase()
  return /^[A-Z]$/.test(firstChar) ? firstChar : OTHER_KEY
}

type BrandDirectoryProps = {
  brands: BrandDirectoryEntry[]
  fetchFailed?: boolean
}

const sortByName = (a: BrandDirectoryEntry, b: BrandDirectoryEntry) =>
  a.name.localeCompare(b.name, "es", { sensitivity: "base" })

export default function BrandDirectory({
  brands,
  fetchFailed = false,
}: BrandDirectoryProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeLetter, setActiveLetter] = useState<string | null>(null)

  const sortedBrands = useMemo(() => [...brands].sort(sortByName), [brands])

  const filteredBrands = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return sortedBrands
    return sortedBrands.filter((brand) =>
      brand.name.toLowerCase().includes(term)
    )
  }, [sortedBrands, searchTerm])

  const sections = useMemo(() => {
    const groups = new Map<string, BrandDirectoryEntry[]>()
    LETTERS.forEach((letter) => groups.set(letter, []))
    groups.set(OTHER_KEY, [])

    filteredBrands.forEach((brand) => {
      const key = getLetterKey(brand.name)
      groups.get(key)?.push(brand)
    })

    return [...LETTERS, OTHER_KEY]
      .map((letter) => ({
        letter,
        entries: [...(groups.get(letter) ?? [])].sort(sortByName),
      }))
      .filter((section) => section.entries.length > 0)
  }, [filteredBrands])

  const availableLetters = useMemo(
    () => new Set(sections.map((s) => s.letter)),
    [sections]
  )

  useEffect(() => {
    if (activeLetter && !availableLetters.has(activeLetter)) {
      setActiveLetter(null)
    }
  }, [activeLetter, availableLetters])

  const handleLetterClick = useCallback(
    (letter: string) => {
      if (!availableLetters.has(letter)) return
      setActiveLetter((current) => {
        const next = current === letter ? null : letter
        if (next) {
          const id = letter === OTHER_KEY ? "letter-other" : `letter-${letter}`
          document
            .getElementById(id)
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
        return next
      })
    },
    [availableLetters]
  )

  return (
    <div>
      {/* ── Hero header ── */}
      <div className="border-b border-black pb-10 mb-10">
        <p className="font-lealti-light text-xs tracking-[0.2em] uppercase text-gray-400 mb-3">
          Directorio
        </p>
        <h1 className="font-lealti text-[clamp(3rem,8vw,7rem)] leading-none tracking-tight text-black uppercase">
          Marcas
        </h1>
        <p className="font-lealti-light mt-4 text-sm tracking-wide text-gray-500 max-w-md">
          Explorá nuestras marcas destacadas y descubrí productos artesanales
          seleccionados por expertos.
        </p>
      </div>

      {/* ── Letter filter ── */}
      <div className="flex flex-wrap gap-x-0.5 gap-y-1 mb-8">
        {[...LETTERS, OTHER_KEY].map((letter) => {
          const isDisabled = !availableLetters.has(letter)
          const isActive = activeLetter === letter
          return (
            <button
              key={letter}
              type="button"
              onClick={() => handleLetterClick(letter)}
              disabled={isDisabled}
              aria-disabled={isDisabled}
              className={[
                "font-lealti-light w-7 h-7 text-[11px] tracking-[0.1em] transition-colors",
                isDisabled
                  ? "text-gray-200 cursor-not-allowed"
                  : isActive
                  ? "text-black underline underline-offset-2"
                  : "text-gray-500 hover:text-black",
              ].join(" ")}
            >
              {letter}
            </button>
          )
        })}
      </div>

      {/* ── Search ── */}
      <div className="mb-12 max-w-xs">
        <div className="relative">
          <input
            id="brand-directory-search"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar marca…"
            className="font-lealti-light w-full border-b border-gray-300 bg-transparent py-2 pr-7 text-sm text-black placeholder:text-gray-400 focus:border-black focus:outline-none transition-colors"
          />
          <svg
            className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-gray-400"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
        </div>
      </div>

      {/* ── Results ── */}
      {sections.length === 0 ? (
        fetchFailed ? (
          <div className="border border-black p-6">
            <p className="font-lealti-light text-sm text-black">
              No pudimos cargar las marcas en este momento.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="font-lealti-light mt-4 text-xs tracking-[0.12em] uppercase underline underline-offset-4 text-black hover:text-gray-500 transition-colors"
            >
              Reintentar
            </button>
          </div>
        ) : (
          <p className="font-lealti-light text-sm text-gray-400">
            No se encontraron marcas para tu búsqueda.
          </p>
        )
      ) : (
        <div>
          {sections.map((section, sectionIndex) => (
            <section
              key={section.letter}
              id={
                section.letter === OTHER_KEY
                  ? "letter-other"
                  : `letter-${section.letter}`
              }
              className={`scroll-mt-24 ${sectionIndex > 0 ? "mt-8" : ""}`}
            >
              {/* letter heading */}
              <div className="border-t border-black pt-3 mb-5">
                <span className="font-lealti-light text-xs tracking-[0.25em] uppercase text-gray-500">
                  {section.letter === OTHER_KEY ? "#" : section.letter}
                </span>
              </div>

              {/* brand grid */}
              <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
                {section.entries.map((brand) => {
                  const summary = brand.reviewSummary
                  const totalReviews =
                    typeof summary?.totalReviews === "number"
                      ? summary.totalReviews
                      : 0
                  const avg =
                    typeof summary?.averageOverall === "number" &&
                    summary.averageOverall > 0
                      ? summary.averageOverall
                      : typeof summary?.vendorReputationScore === "number"
                      ? summary.vendorReputationScore
                      : 0

                  return (
                    <li
                      key={brand.slug}
                      className="border-t border-gray-200 py-3 pr-4"
                    >
                      <LocalizedClientLink
                        href={`/${brand.slug}`}
                        className="group flex flex-col gap-0.5"
                      >
                        <span className="font-lealti-light text-[15px] text-black tracking-wide leading-snug group-hover:underline underline-offset-4 transition-all">
                          {brand.name}
                        </span>
                        {totalReviews > 0 && avg > 0 && (
                          <span className="font-lealti-light text-[10px] tracking-wide text-gray-400">
                            {avg.toFixed(1)} · {totalReviews}{" "}
                            {totalReviews === 1 ? "reseña" : "reseñas"}
                          </span>
                        )}
                        {brand.verifiedVendor && (
                          <span className="font-lealti-light text-[10px] tracking-[0.1em] uppercase text-gray-400">
                            Verificada
                          </span>
                        )}
                      </LocalizedClientLink>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
